import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getActiveClientPolicySnapshot,
  getClientPolicies,
  getEffectiveThresholdsSnapshot,
  savePolicyThresholdValues,
} from '../../services/complianceRules';
import { getThresholdKeyLabel } from '../../services/complianceTranslations';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { routePaths } from '../../config/navigation';

const RISK_THRESHOLD_KEYS = [
  'risk_medium_min_alert_count',
  'risk_medium_min_total_alert_value',
  'risk_medium_on_any_alert',
  'risk_high_min_alert_count',
  'risk_high_min_total_alert_value',
  'risk_high_on_high_severity_alert',
];

const disabledButtonSx = {
  '&.MuiButton-contained.Mui-disabled': {
    color: 'text.disabled',
    backgroundColor: 'action.disabledBackground',
    borderColor: 'action.disabledBackground',
    opacity: 1,
    WebkitTextFillColor: 'currentColor',
  },
};

const MEDIUM_RISK_KEYS = [
  'risk_medium_min_alert_count',
  'risk_medium_min_total_alert_value',
  'risk_medium_on_any_alert',
];

const HIGH_RISK_KEYS = [
  'risk_high_min_alert_count',
  'risk_high_min_total_alert_value',
  'risk_high_on_high_severity_alert',
];

const parseNumericValue = (value, valueType) => {
  if (valueType === 'integer') {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (valueType === 'float') {
    const parsed = Number.parseFloat(String(value));
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const buildThresholdLimitError = (key, limits, draftValue) => {
  if (!limits || typeof limits !== 'object') return '';
  const { valueType } = limits;
  if (valueType !== 'integer' && valueType !== 'float') return '';

  const numericValue = parseNumericValue(draftValue, valueType);

  if (numericValue === null) {
    return `${getThresholdKeyLabel(key)}: valor inválido para tipo ${valueType}.`;
  }

  if (limits.min !== null && limits.min !== undefined && numericValue < Number(limits.min)) {
    return `${getThresholdKeyLabel(key)}: valor mínimo é ${limits.min}.`;
  }

  if (limits.max !== null && limits.max !== undefined && numericValue > Number(limits.max)) {
    return `${getThresholdKeyLabel(key)}: valor máximo é ${limits.max}.`;
  }

  return '';
};

const isClientPolicyNotFoundError = (error) => {
  const status = error?.response?.status || error?.status;
  const rawMessage = String(
    error?.response?.data?.error
      || error?.response?.data?.message
      || error?.message
      || ''
  ).toLowerCase();

  return status === 404 && rawMessage.includes('client policy not found');
};

export default function RulesPlanRiskEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPolicyId = React.useMemo(() => new URLSearchParams(location.search).get('policyId') || '', [location.search]);
  const selectedOrganizationId = React.useMemo(() => new URLSearchParams(location.search).get('organizationId') || '', [location.search]);

  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [saveMessage, setSaveMessage] = React.useState('');
  const [saveErrorMessage, setSaveErrorMessage] = React.useState('');
  const [savingThresholdChanges, setSavingThresholdChanges] = React.useState(false);
  const [policy, setPolicy] = React.useState(null);
  const [thresholdValuesBaseline, setThresholdValuesBaseline] = React.useState({});
  const [thresholdValuesDraft, setThresholdValuesDraft] = React.useState({});
  const [thresholdLimitsByKey, setThresholdLimitsByKey] = React.useState({});

  const effectiveOrganizationId = React.useMemo(
    () => selectedOrganizationId || policy?.organizationId || policy?.raw?.organization_id || '',
    [policy, selectedOrganizationId],
  );

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        let effectivePolicy = null;

        if (selectedPolicyId) {
          try {
            const policies = await getClientPolicies({ scopeType: 'organization', organizationId: selectedOrganizationId });
            if (!isMounted) return;

            const selectedPolicy = policies.find((item) => item.id === selectedPolicyId);
            if (selectedPolicy) {
              effectivePolicy = selectedPolicy;
            } else {
              const snapshot = await getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: selectedOrganizationId });
              if (!isMounted) return;
              effectivePolicy = snapshot.policy;
            }
          } catch (error) {
            if (!isClientPolicyNotFoundError(error)) throw error;

            const snapshot = await getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: selectedOrganizationId });
            if (!isMounted) return;
            effectivePolicy = snapshot.policy;
          }
        } else {
          const snapshot = await getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: selectedOrganizationId });
          if (!isMounted) return;
          effectivePolicy = snapshot.policy;
        }

        setPolicy(effectivePolicy);

        if (!effectivePolicy?.id) {
          setThresholdValuesBaseline({});
          setThresholdValuesDraft({});
          setThresholdLimitsByKey({});
          return;
        }

        const snap = await getEffectiveThresholdsSnapshot(RISK_THRESHOLD_KEYS, '', effectivePolicy.id, '');
        if (!isMounted) return;

        setThresholdValuesBaseline({ ...snap.valuesByKey });
        setThresholdValuesDraft({ ...snap.valuesByKey });
        setThresholdLimitsByKey({ ...(snap.thresholdLimitsByKey || {}) });
      } catch (error) {
        if (isMounted) {
          setPolicy(null);
          setThresholdValuesBaseline({});
          setThresholdValuesDraft({});
          setThresholdLimitsByKey({});
          setErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [selectedOrganizationId, selectedPolicyId]);

  const pendingThresholdChanges = React.useMemo(() => {
    const allKeys = Array.from(new Set([
      ...Object.keys(thresholdValuesBaseline),
      ...Object.keys(thresholdValuesDraft),
    ]));

    return allKeys.reduce((changes, key) => {
      const baseline = thresholdValuesBaseline[key] ?? '';
      const draft = thresholdValuesDraft[key] ?? '';

      if (String(baseline) !== String(draft)) {
        changes.push({ key, baselineValue: baseline, draftValue: draft });
      }

      return changes;
    }, []);
  }, [thresholdValuesBaseline, thresholdValuesDraft]);

  const savingAny = Boolean(savingThresholdChanges);

  const buildThresholdHelperText = React.useCallback((key) => ([
    thresholdLimitsByKey[key]?.min !== null && thresholdLimitsByKey[key]?.min !== undefined
      ? `Min: ${thresholdLimitsByKey[key].min}`
      : null,
    thresholdLimitsByKey[key]?.max !== null && thresholdLimitsByKey[key]?.max !== undefined
      ? `Max: ${thresholdLimitsByKey[key].max}`
      : null,
    thresholdLimitsByKey[key]?.step !== null && thresholdLimitsByKey[key]?.step !== undefined
      ? `Passo: ${thresholdLimitsByKey[key].step}`
      : null,
    thresholdLimitsByKey[key]?.defaultValue !== null && thresholdLimitsByKey[key]?.defaultValue !== undefined
      ? `Padrão: ${thresholdLimitsByKey[key].defaultValue}`
      : null,
  ].filter(Boolean).join(' | ')), [thresholdLimitsByKey]);

  const handleThresholdValueChange = React.useCallback((key, value) => {
    setThresholdValuesDraft((current) => ({ ...current, [key]: value }));
    setSaveMessage('');
    setSaveErrorMessage('');
  }, []);

  const handleDiscardChanges = React.useCallback(() => {
    setThresholdValuesDraft({ ...thresholdValuesBaseline });
    setSaveMessage('');
    setSaveErrorMessage('');
  }, [thresholdValuesBaseline]);

  const handleSaveThresholdChanges = React.useCallback(async () => {
    if (!policy?.id || !pendingThresholdChanges.length) return;

    setSavingThresholdChanges(true);
    setSaveMessage('');
    setSaveErrorMessage('');

    try {
      const invalidChange = pendingThresholdChanges.find((change) => {
        const limits = thresholdLimitsByKey[change.key];
        return Boolean(buildThresholdLimitError(change.key, limits, change.draftValue));
      });

      if (invalidChange) {
        const limits = thresholdLimitsByKey[invalidChange.key];
        setSaveErrorMessage(buildThresholdLimitError(invalidChange.key, limits, invalidChange.draftValue));
        return;
      }

      const summary = await savePolicyThresholdValues(
        policy.id,
        pendingThresholdChanges,
        'Ajuste de risco global via tela de policy',
      );

      if (summary.failed.length) {
        const failureDetails = summary.failed
          .map((item) => getRulesEngineUiMessage(item.error, 'Falha desconhecida.'))
          .join('; ');
        setSaveErrorMessage(
          `Nem todas as alterações de risco foram salvas (${summary.successful.length} de ${summary.successful.length + summary.failed.length} salvas): ${failureDetails}`,
        );
      }

      if (summary.successful.length) {
        setThresholdValuesBaseline((current) => {
          const next = { ...current };
          summary.successful.forEach((item) => {
            if (item?.change?.key) {
              next[item.change.key] = thresholdValuesDraft[item.change.key];
            }
          });
          return next;
        });
        setSaveMessage(`Configuração global de risco salva com sucesso (${summary.successful.length}).`);
      }
    } catch (error) {
      setSaveErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSavingThresholdChanges(false);
    }
  }, [pendingThresholdChanges, policy, thresholdLimitsByKey, thresholdValuesDraft]);

  const backToOverviewPath = React.useMemo(() => {
    const query = new URLSearchParams();
    if (effectiveOrganizationId) query.set('organizationId', effectiveOrganizationId);
    const queryString = query.toString();
    return queryString ? `${routePaths.rulesPlan}?${queryString}` : routePaths.rulesPlan;
  }, [effectiveOrganizationId]);

  const renderThresholdField = React.useCallback((key) => {
    const valueType = thresholdLimitsByKey[key]?.valueType;
    const commonProps = {
      label: getThresholdKeyLabel(key),
      size: 'small',
      value: thresholdValuesDraft[key] ?? '',
      onChange: (event) => handleThresholdValueChange(key, event.target.value),
      disabled: savingAny,
      helperText: buildThresholdHelperText(key),
      fullWidth: true,
    };

    if (valueType === 'boolean') {
      return (
        <TextField key={`risk-${key}`} {...commonProps} select>
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Não</MenuItem>
        </TextField>
      );
    }

    return (
      <TextField
        key={`risk-${key}`}
        {...commonProps}
        type={valueType === 'integer' || valueType === 'float' ? 'number' : 'text'}
        inputProps={{
          min: thresholdLimitsByKey[key]?.min ?? undefined,
          max: thresholdLimitsByKey[key]?.max ?? undefined,
          step: thresholdLimitsByKey[key]?.step ?? undefined,
        }}
      />
    );
  }, [buildThresholdHelperText, handleThresholdValueChange, savingAny, thresholdLimitsByKey, thresholdValuesDraft]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando configuração global de risco...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button variant="outlined" size="small" disableRipple onClick={() => navigate(backToOverviewPath)}>
            ← Regras da Policy
          </Button>
          <Typography variant="h6">Risco Global da Policy</Typography>
        </Stack>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {!loading && !errorMessage && !policy ? (
          <Alert severity="info">Esta organização não possui policy ativa para edição.</Alert>
        ) : null}
        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveErrorMessage ? <Alert severity="warning">{saveErrorMessage}</Alert> : null}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1}>
              <Typography component="h1" variant="h5">Configuração global de risco</Typography>
              {policy ? (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Política: {policy.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Versão: {policy.version}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Cobertura: {policy.coverage}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nenhuma política ativa encontrada no momento.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h2" variant="h6">Limiares globais de risco</Typography>

              {pendingThresholdChanges.length ? (
                <Alert severity="info">
                  Alterações pendentes: {pendingThresholdChanges.length}
                </Alert>
              ) : (
                <Alert severity="info">Nenhuma alteração pendente.</Alert>
              )}

              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Condições para elevar ao risco médio
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Esses limiares definem quando a policy promove uma transação para risco médio.
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  {MEDIUM_RISK_KEYS.map((key) => (
                    <Grid key={`medium-${key}`} size={{ xs: 12, md: 4 }}>
                      {renderThresholdField(key)}
                    </Grid>
                  ))}
                </Grid>

                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Condições para elevar ao risco alto
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Esses limiares definem quando a policy promove uma transação diretamente para risco alto.
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  {HIGH_RISK_KEYS.map((key) => (
                    <Grid key={`high-${key}`} size={{ xs: 12, md: 4 }}>
                      {renderThresholdField(key)}
                    </Grid>
                  ))}
                </Grid>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  disableRipple
                  onClick={handleDiscardChanges}
                  disabled={savingAny || !pendingThresholdChanges.length}
                >
                  Descartar alterações
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disableRipple
                  onClick={handleSaveThresholdChanges}
                  disabled={savingAny || !pendingThresholdChanges.length || !policy?.id}
                  sx={disabledButtonSx}
                >
                  {savingThresholdChanges ? 'Salvando risco global...' : 'Salvar risco global'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
